import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { db } from "../../firebase/firebase";
import { collection, getDocs } from "firebase/firestore";
import Banner from "../../images/banner.jpg";
import "./ShopHome.css";
import Footer from "../Footer/Footer";
import NavBar from "../Layout/NavBar";

const ShopHome = () => {
  const [recentPosts, setRecentPosts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [trendingBlogs, setTrendingBlogs] = useState([]);

  useEffect(() => {
    async function loadPosts() {
      const blogsSnap = await getDocs(collection(db, "blogs"));
      const analyticsSnap = await getDocs(collection(db, "blogAnalytics"));

      const blogs = blogsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const analytics = analyticsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // Merge analytics into blogs
      const merged = blogs.map((b) => {
        const a = analytics.find((x) => x.id === b.id) || {};
        return {
          ...b,
          likes: a.likes || 0,
          views: a.views || 0,
          lastLiked: a.lastLiked || null,
        };
      });

      // Sort newest → oldest
      const sorted = merged
        .sort((a, b) => b.createdAt.seconds - a.createdAt.seconds)
        .slice(0, 3);

      setRecentPosts(sorted);
    }

    loadPosts();
  }, []);

  useEffect(() => {
    async function loadFeatured() {
      const productsSnap = await getDocs(collection(db, "products"));
      const analyticsSnap = await getDocs(collection(db, "analyticsProducts"));

      const products = productsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const analytics = analyticsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // Merge analytics into products
      const merged = products.map((p) => {
        const a = analytics.find((x) => x.id === p.id) || {};
        return {
          ...p,
          likes: a.likes || 0,
          views: a.views || 0,
        };
      });

      // Sort by likes
      const sorted = merged
        .sort((a, b) => b.likes - a.likes)
        .slice(0, 4);

      setFeaturedProducts(sorted);
    }

    loadFeatured();
  }, []);

  useEffect(() => {
    async function loadTrendingBlogs() {
      const blogsSnap = await getDocs(collection(db, "blogs"));
      const analyticsSnap = await getDocs(collection(db, "blogAnalytics"));

      const blogs = blogsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const analytics = analyticsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // Merge analytics into blogs
      const merged = blogs.map((b) => {
        const a = analytics.find((x) => x.id === b.id) || {};
        return {
          ...b,
          likes: a.likes || 0,
          views: a.views || 0,
        };
      });

      // Sort by likes
      const sorted = merged
        .sort((a, b) => b.likes - a.likes)
        .slice(0, 3);

      setTrendingBlogs(sorted);
    }

    loadTrendingBlogs();
  }, []);

  function timeAgo(ts) {
    const date = ts.toDate();
    const diff = (Date.now() - date.getTime()) / 1000; // seconds

    const minutes = diff / 60;
    const hours = diff / 3600;
    const days = diff / 86400;
    const months = diff / (86400 * 30);   // approx
    const years = diff / (86400 * 365);   // approx

    if (diff < 60) return "Just now";
    if (minutes < 60) return `${Math.floor(minutes)}m ago`;
    if (hours < 24) return `${Math.floor(hours)}h ago`;
    if (days < 30) return `${Math.floor(days)}d ago`;
    if (months < 2) return `${Math.floor(months)}mo ago`;
    if (months < 12) return `${Math.floor(months)}mos ago`;
    return `${Math.floor(years)}y ago`;
  }


  return (
    <div className="shop-home">
      <div className="shop-banner">
        <img src={Banner} alt="Shop Banner" />
      </div>

      <NavBar />

      <div className="shop-content-container">

        {/* ⭐ Highlights Section */}
        <section className="highlights">
            <h2>What We Create</h2>
            <p>Coloring books, journals, stories, and creative tools for all ages.</p>

            <div className="highlight-grid">
              <div className="highlight-card">Built for Creativity</div>
              <div className="highlight-card">Inspired by Imagination</div>
              <div className="highlight-card">Made to Spark Inspiration</div>
            </div>
        </section>

        {/* ⭐ Featured Products */}
        <section className="featured-products">
          <h2>Featured Products</h2>

          <div className="featured-grid">
            {featuredProducts.map((p) => (
              <Link key={p.id} to={`/shop/${p.category}/${p.id}`} className="featured-card">

                {/* Image */}
                {p.image && (
                  <img src={p.image} alt={p.title} className="blog-thumb" />
                )}

                <h3>{p.title.replace(/for kids/i, "").trim()}</h3>

                <div className="home-meta-row">
                  <div className="home-tags">
                    {p.tags?.slice(0, 3).map((tag, i) => (
                      <span key={i} className="home-tag">{tag}</span>
                    ))}
                  </div>

                  <div className="home-likes">
                    {p.likes || 0}
                  </div>
                </div>

                <div className="summary">
                  <p>{p.description?.slice(0, 130)}...</p>
                </div>

                <div className="product-footer">
                  <span className="product-date">{timeAgo(p.createdAt)}</span>
                </div>

              </Link>
            ))}
          </div>
        </section>


        {/* ⭐ Trending Blogs */}
        <section className="trending-blogs">
          <h2>Trending Blogs</h2>

          <div className="trending-grid">
            {trendingBlogs.map((post) => (
              <Link key={post.id} to={`/blog/${post.id}`} className="trending-card">

                {/* Image or Video */}
                {post.video ? (
                  <video
                    src={post.video}
                    poster={post.headerImage}
                    className="blog-thumb"
                    muted
                  />
                ) : (
                  post.headerImage && (
                    <img src={post.headerImage} className="blog-thumb" alt={post.title} />
                  )
                )}

                <h3>{post.title}</h3>

                <div className="home-meta-row">
                  <div className="home-tags">
                    {post.tags?.slice(0, 3).map((tag, i) => (
                      <span key={i} className="home-tag">{tag}</span>
                    ))}
                  </div>

                  <div className="home-likes">
                    {post.likes || 0}
                  </div>
                </div>

                <div className="summary">
                  <p>{post.summary}</p>
                </div>

                <div className="product-footer">
                  <span className="product-date">{timeAgo(post.createdAt)}</span>
                </div>

              </Link>
            ))}
          </div>
        </section>


        {/* ⭐ Recent Blog Posts */}
        <section className="recent-posts">
            <h2>Recent Posts</h2>

            <div className="recent-posts-grid">
                {recentPosts.map((post) => (
                    <Link key={post.id} to={`/blog/${post.id}`} className="recent-post-card">
                    {post.images?.[0] && (
                        <img src={post.images[0]} alt={post.title} />
                    )}
                    <h3>{post.title}</h3>
                    {/* Video first */}
                    {post.video && (
                      <video
                        src={post.video}
                        poster={post.headerImage}
                        className="blog-thumb"
                        muted
                      />
                    )}

                    {/* Image fallback */}
                    {!post.video && post.headerImage && (
                      <img src={post.headerImage} className="blog-thumb" alt="" />
                    )}

                    <div className="home-meta-row">
                      <div className="home-tags">
                        {post.tags?.slice(0, 3).map((tag, i) => (
                          <span key={i} className="home-tag">{tag}</span>
                        ))}
                      </div>

                      <div className="home-likes">
                        {post.likes || 0}
                      </div>
                    </div>

                    <div className="summary">
                      <p>
                        {post.summary}
                      </p>
                    </div>

                    <div className="product-footer">
                      <span className="product-date">{timeAgo(post.createdAt)}</span>
                    </div>

                  </Link>
                ))}
            </div>
        </section>
      </div>
      <Footer />
    </div>
  );
};

export default ShopHome;
